import React, { useState, useEffect, useRef } from 'react';
import {
  Settings,
  Save,
  Loader2,
  Upload,
  Trash2,
  Image,
  Type,
  Building2,
  Palette,
  Monitor,
  RotateCcw
} from 'lucide-react';
import {
  getBrandingConfig,
  updateBrandingConfig,
  deleteBrandingLogo
} from '../api/brandingSettings';
import { useBranding } from '../contexts/BrandingContext';
import { useModule } from '../contexts/ModuleContext';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SearchableSelect from '../components/ui/SearchableSelect';
import { CURRENCIES, getCurrencySymbol } from '../utils/currency';

const AFRICA_TIMEZONES = [
  {
    label: 'UTC+0 · GMT — West / Atlantic Africa',
    zones: [
      {
        value: 'Africa/Abidjan',
        label:
          "Abidjan — Côte d'Ivoire · Burkina Faso · Gambia · Guinea · Guinea-Bissau · Liberia · Mali · Mauritania · Senegal · Sierra Leone · Togo"
      },
      { value: 'Africa/Accra', label: 'Accra — Ghana' },
      { value: 'Africa/Sao_Tome', label: 'São Tomé — São Tomé & Príncipe' }
    ]
  },
  {
    label: 'UTC+1 · WAT — West Africa Time',
    zones: [
      { value: 'Africa/Lagos', label: 'Lagos — Nigeria' },
      { value: 'Africa/Luanda', label: 'Luanda — Angola' },
      { value: 'Africa/Bangui', label: 'Bangui — Central African Republic' },
      { value: 'Africa/Brazzaville', label: 'Brazzaville — Republic of Congo' },
      { value: 'Africa/Kinshasa', label: 'Kinshasa — DR Congo (West)' },
      { value: 'Africa/Douala', label: 'Douala — Cameroon' },
      { value: 'Africa/Libreville', label: 'Libreville — Gabon' },
      { value: 'Africa/Malabo', label: 'Malabo — Equatorial Guinea' },
      { value: 'Africa/Ndjamena', label: 'Ndjamena — Chad' },
      { value: 'Africa/Niamey', label: 'Niamey — Niger' },
      { value: 'Africa/Porto-Novo', label: 'Porto-Novo — Benin' }
    ]
  },
  {
    label: 'UTC+1 · CET / WET — North Africa',
    zones: [
      { value: 'Africa/Algiers', label: 'Algiers — Algeria' },
      { value: 'Africa/Tunis', label: 'Tunis — Tunisia' },
      { value: 'Africa/Casablanca', label: 'Casablanca — Morocco' },
      { value: 'Africa/El_Aaiun', label: 'El Aaiun — Western Sahara' }
    ]
  },
  {
    label: 'UTC+2 · CAT — Central Africa Time',
    zones: [
      { value: 'Africa/Johannesburg', label: 'Johannesburg — South Africa' },
      { value: 'Africa/Maseru', label: 'Maseru — Lesotho' },
      { value: 'Africa/Mbabane', label: 'Mbabane — Eswatini' },
      { value: 'Africa/Harare', label: 'Harare — Zimbabwe' },
      { value: 'Africa/Lusaka', label: 'Lusaka — Zambia' },
      { value: 'Africa/Maputo', label: 'Maputo — Mozambique' },
      { value: 'Africa/Blantyre', label: 'Blantyre — Malawi' },
      { value: 'Africa/Gaborone', label: 'Gaborone — Botswana' },
      { value: 'Africa/Lubumbashi', label: 'Lubumbashi — DR Congo (East)' },
      { value: 'Africa/Kigali', label: 'Kigali — Rwanda' },
      { value: 'Africa/Bujumbura', label: 'Bujumbura — Burundi' },
      { value: 'Africa/Windhoek', label: 'Windhoek — Namibia' }
    ]
  },
  {
    label: 'UTC+2 · EET — North-East Africa',
    zones: [
      { value: 'Africa/Cairo', label: 'Cairo — Egypt' },
      { value: 'Africa/Tripoli', label: 'Tripoli — Libya' }
    ]
  },
  {
    label: 'UTC+3 · EAT — East Africa Time',
    zones: [
      { value: 'Africa/Nairobi', label: 'Nairobi — Kenya' },
      { value: 'Africa/Dar_es_Salaam', label: 'Dar es Salaam — Tanzania' },
      { value: 'Africa/Kampala', label: 'Kampala — Uganda' },
      { value: 'Africa/Addis_Ababa', label: 'Addis Ababa — Ethiopia' },
      { value: 'Africa/Asmara', label: 'Asmara — Eritrea' },
      { value: 'Africa/Mogadishu', label: 'Mogadishu — Somalia' },
      { value: 'Africa/Djibouti', label: 'Djibouti — Djibouti' },
      { value: 'Africa/Juba', label: 'Juba — South Sudan' },
      { value: 'Africa/Khartoum', label: 'Khartoum — Sudan' },
      { value: 'Africa/Antananarivo', label: 'Antananarivo — Madagascar' },
      { value: 'Indian/Comoro', label: 'Moroni — Comoros' },
      { value: 'Indian/Mayotte', label: 'Mayotte — Mayotte' }
    ]
  },
  {
    label: 'UTC+4 · Indian Ocean Islands',
    zones: [
      { value: 'Indian/Mauritius', label: 'Port Louis — Mauritius' },
      { value: 'Indian/Mahe', label: 'Mahé — Seychelles' },
      { value: 'Indian/Reunion', label: 'Saint-Denis — Réunion' }
    ]
  }
];

const CURRENCY_OPTIONS = Object.values(CURRENCIES).map((c) => ({
  value: c.code,
  label: `${c.code} — ${c.symbol} — ${c.name}`
}));

const TIMEZONE_OPTIONS = [
  ...AFRICA_TIMEZONES.map((g) => ({
    group: g.label,
    items: g.zones.map((z) => ({ value: z.value, label: z.label }))
  })),
  {
    group: 'Other Timezones',
    items: [
      { value: 'UTC', label: 'UTC — Coordinated Universal Time' },
      { value: 'Europe/London', label: 'London — UK (GMT/BST)' },
      { value: 'America/New_York', label: 'New York — Eastern Time (ET)' },
      { value: 'America/Chicago', label: 'Chicago — Central Time (CT)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' }
    ]
  }
];

/**
 * BrandingSettingsPage Component
 * Admin interface for customizing company branding (logo, company name)
 */
const BrandingSettingsPage = () => {
  const { activeModule } = useModule();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [wholesaleLogoPreview, setWholesaleLogoPreview] = useState(null);
  const [wholesaleLogoFile, setWholesaleLogoFile] = useState(null);
  const wholesaleFileInputRef = useRef(null);

  const [config, setConfig] = useState({
    company_name: 'Eldaved Global',
    company_email: 'info@company.com',
    company_phone: '+234 800 000 0000',
    company_address: '',
    currency: 'NGN',
    smart_decimals: true,
    timezone: 'Africa/Lagos',
    date_format: 'DD/MM/YYYY',
    logo_url: null,
    logo_type: 'icon', // 'icon', 'full', 'none'
    logo_icon_text: 'BMS',
    use_logo_as_watermark: false,
    watermark_opacity: 0.1,
    primary_color: '#1A1A1A',
    accent_color: '#D4AF37',
    text_color: '#333333',
    background_color: '#FFFFFF',
    sidebar_name_size: 'medium', // 'small', 'medium', 'large'
    // App UI colors
    app_primary_color: '#1A1A1A',
    app_accent_color: '#D4AF37',
    app_accent_light: '#FFD700',
    app_accent_dark: '#C5A353',
    app_success_color: '#10B981',
    app_warning_color: '#F59E0B',
    app_danger_color: '#EF4444',
    enable_custom_app_colors: false,
    // Wholesale Module colors
    wholesale_primary_color: '#047857',
    wholesale_accent_color: '#10B981',
    wholesale_border_color: '#065F46',
    wholesale_hover_color: '#065F46',
    wholesale_text_color: '#D1FAE5',
    enable_custom_wholesale_colors: false,
    // Retail Module colors
    retail_primary_color: '#1A1A1A',
    retail_accent_color: '#D4AF37',
    retail_border_color: '#2D2D2D',
    retail_hover_color: '#2D2D2D',
    retail_text_color: '#E5E5E5',
    enable_custom_retail_colors: false,
    // Separate wholesale branding
    use_separate_wholesale_branding: false,
    wholesale_company_name: '',
    wholesale_company_email: '',
    wholesale_company_phone: '',
    wholesale_company_address: '',
    wholesale_currency: 'NGN',
    wholesale_smart_decimals: true,
    wholesale_timezone: 'Africa/Lagos',
    wholesale_date_format: 'DD/MM/YYYY',
    wholesale_logo_url: null,
    wholesale_logo_type: 'icon',
    wholesale_logo_icon_text: 'WS',
    wholesale_use_logo_as_watermark: false,
    wholesale_watermark_opacity: 0.1,
    wholesale_sidebar_name_size: 'medium'
  });

  // Get branding context for refreshing after save
  const { refreshBranding, updateConfigLocally } = useBranding();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getBrandingConfig();
      setConfig(response.data);
      if (response.data.logo_url) {
        setLogoPreview(response.data.logo_url);
      }
      if (response.data.wholesale_logo_url) {
        setWholesaleLogoPreview(response.data.wholesale_logo_url);
      }
    } catch (error) {
      toast.error('Failed to load branding configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select an image file (jpeg, png, gif, webp)');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveLogo = async () => {
    if (!config.logo_url && !logoFile) {
      toast.info('No logo to remove');
      return;
    }

    // If there's a saved logo, delete it from server
    if (config.logo_url) {
      setDeleting(true);
      try {
        await deleteBrandingLogo();
        setConfig((prev) => ({ ...prev, logo_url: null }));
        toast.success('Logo removed successfully');
      } catch (error) {
        toast.error('Failed to remove logo');
        setDeleting(false);
        return;
      }
      setDeleting(false);
    }

    // Clear local state
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleWholesaleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select an image file (jpeg, png, gif, webp)');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      setWholesaleLogoFile(file);
      setWholesaleLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveWholesaleLogo = () => {
    // Clear wholesale logo from config
    setConfig((prev) => ({ ...prev, wholesale_logo_url: null }));

    // Clear local state
    setWholesaleLogoFile(null);
    setWholesaleLogoPreview(null);
    if (wholesaleFileInputRef.current) {
      wholesaleFileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formData = new FormData();

      // Add logo file if selected
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      // Add other fields
      formData.append('company_name', config.company_name);
      formData.append('logo_type', config.logo_type);
      formData.append('logo_icon_text', config.logo_icon_text);
      formData.append('use_logo_as_watermark', config.use_logo_as_watermark);
      formData.append('watermark_opacity', config.watermark_opacity);
      formData.append('primary_color', config.primary_color);
      formData.append('accent_color', config.accent_color);
      formData.append('text_color', config.text_color);
      formData.append('background_color', config.background_color);
      formData.append('sidebar_name_size', config.sidebar_name_size);

      // App UI colors
      formData.append('app_primary_color', config.app_primary_color);
      formData.append('app_accent_color', config.app_accent_color);
      formData.append('app_accent_light', config.app_accent_light);
      formData.append('app_accent_dark', config.app_accent_dark);
      formData.append('app_success_color', config.app_success_color);
      formData.append('app_warning_color', config.app_warning_color);
      formData.append('app_danger_color', config.app_danger_color);
      formData.append('enable_custom_app_colors', config.enable_custom_app_colors);

      // Wholesale Module colors
      formData.append('wholesale_primary_color', config.wholesale_primary_color);
      formData.append('wholesale_accent_color', config.wholesale_accent_color);
      formData.append('wholesale_border_color', config.wholesale_border_color);
      formData.append('wholesale_hover_color', config.wholesale_hover_color);
      formData.append('wholesale_text_color', config.wholesale_text_color);
      formData.append('enable_custom_wholesale_colors', config.enable_custom_wholesale_colors);

      // Retail Module colors
      formData.append('retail_primary_color', config.retail_primary_color);
      formData.append('retail_accent_color', config.retail_accent_color);
      formData.append('retail_border_color', config.retail_border_color);
      formData.append('retail_hover_color', config.retail_hover_color);
      formData.append('retail_text_color', config.retail_text_color);
      formData.append('enable_custom_retail_colors', config.enable_custom_retail_colors);

      // Separate wholesale branding
      formData.append('use_separate_wholesale_branding', config.use_separate_wholesale_branding);
      if (config.use_separate_wholesale_branding) {
        if (wholesaleLogoFile) {
          formData.append('wholesale_logo', wholesaleLogoFile);
        } else {
          // Explicitly send the wholesale_logo_url to handle logo removal
          // If config.wholesale_logo_url is null, this sends empty string to clear it
          formData.append('wholesale_logo_url', config.wholesale_logo_url || '');
        }
        formData.append('wholesale_company_name', config.wholesale_company_name || '');
        formData.append('wholesale_logo_type', config.wholesale_logo_type);
        formData.append('wholesale_logo_icon_text', config.wholesale_logo_icon_text);
        formData.append('wholesale_use_logo_as_watermark', config.wholesale_use_logo_as_watermark);
        formData.append('wholesale_watermark_opacity', config.wholesale_watermark_opacity);
        formData.append('wholesale_sidebar_name_size', config.wholesale_sidebar_name_size);
      }

      await updateBrandingConfig(formData);
      toast.success('Branding configuration updated successfully');

      // Clear file state and refresh config
      setLogoFile(null);
      setWholesaleLogoFile(null);
      fetchConfig();

      // Refresh app-wide branding colors
      await refreshBranding();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading settings..." className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-accent-gold" />
            <h1 className="text-xl sm:text-2xl font-bold text-primary-black">Branding Settings</h1>
          </div>
          <p className="text-secondary-gray mt-2 text-sm">
            Customize your company logo and name. These settings apply to the sidebar, receipts,
            reports, WhatsApp messages, emails, and all printable content.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4">Company Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={config.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  required
                  placeholder="Enter company name"
                />
                <p className="text-xs text-secondary-gray mt-1">
                  This will also update the company name in receipt settings.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Company Email
                </label>
                <input
                  type="email"
                  value={config.company_email || ''}
                  onChange={(e) => handleInputChange('company_email', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  placeholder="info@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Company Phone
                </label>
                <input
                  type="text"
                  value={config.company_phone || ''}
                  onChange={(e) => handleInputChange('company_phone', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  placeholder="+234 800 000 0000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Currency
                </label>
                <SearchableSelect
                  value={config.currency || 'NGN'}
                  onChange={(val) => handleInputChange('currency', val)}
                  options={CURRENCY_OPTIONS}
                  placeholder="Select currency"
                  searchPlaceholder="Search currencies…"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Currency Symbol
                </label>
                <input
                  type="text"
                  readOnly
                  value={getCurrencySymbol(config.currency || 'NGN')}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg bg-gray-50 opacity-70 cursor-default"
                  tabIndex={-1}
                />
              </div>

              <div className="md:col-span-2 py-2 border-y border-gray-100 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm text-primary-black">Smart Currency Decimals</p>
                  <p className="text-xs text-secondary-gray mt-0.5">
                    Hide .00 decimals but show non-zero decimals (e.g. ₦1,000.50 stays, ₦1,000.00
                    becomes ₦1,000)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange('smart_decimals', !config.smart_decimals)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${config.smart_decimals ? 'bg-accent-gold' : 'bg-gray-200'}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${config.smart_decimals ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Company Address
                </label>
                <textarea
                  value={config.company_address || ''}
                  onChange={(e) => handleInputChange('company_address', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none"
                  placeholder="Enter company address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Timezone
                </label>
                <SearchableSelect
                  value={config.timezone || 'Africa/Lagos'}
                  onChange={(val) => handleInputChange('timezone', val)}
                  options={TIMEZONE_OPTIONS}
                  placeholder="Select timezone"
                  searchPlaceholder="Search timezones…"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Date Format
                </label>
                <select
                  value={config.date_format || 'DD/MM/YYYY'}
                  onChange={(e) => handleInputChange('date_format', e.target.value)}
                  className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold bg-white"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>

              {/* Sidebar Text Size */}
              <div className="md:col-span-2 pt-2">
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Sidebar Text Size
                </label>
                <div className="flex bg-gray-100 p-1 rounded-lg w-full sm:w-80">
                  {[
                    { value: 'small', label: 'Small' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'large', label: 'Large' }
                  ].map((size) => (
                    <button
                      key={size.value}
                      type="button"
                      onClick={() => handleInputChange('sidebar_name_size', size.value)}
                      className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                        config.sidebar_name_size === size.value
                          ? 'bg-accent-gold text-white shadow-sm'
                          : 'text-secondary-gray hover:text-primary-black'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-secondary-gray mt-1">
                  Controls the company name text size in the sidebar. Text wraps automatically if
                  too long.
                </p>
              </div>
            </div>
          </div>

          {/* Separate Wholesale Branding */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4">
              Module-Specific Branding
            </h2>

            {/* Toggle for separate wholesale branding */}
            <label className="flex items-center gap-3 p-3 mb-4 border border-secondary-lightGray rounded-lg hover:bg-secondary-offWhite transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={config.use_separate_wholesale_branding}
                onChange={(e) =>
                  handleInputChange('use_separate_wholesale_branding', e.target.checked)
                }
                className="w-4 h-4 text-emerald-600 focus:ring-emerald-600 border-secondary-lightGray rounded"
              />
              <div>
                <span className="text-sm font-medium text-primary-black">
                  Use Separate Branding for Wholesale Module
                </span>
                <p className="text-xs text-secondary-gray">
                  When enabled, the wholesale module will have its own logo, company name, and
                  branding settings independent from the retail module.
                </p>
              </div>
            </label>

            {/* Wholesale Branding Fields (shown when toggle is enabled) */}
            {config.use_separate_wholesale_branding && (
              <div className="space-y-6 mt-6 p-4 border-2 border-emerald-500 bg-emerald-50/30 rounded-r-lg">
                <h3 className="text-md font-semibold text-emerald-700 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Wholesale Module Branding
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Wholesale Company Name */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Wholesale Company Name
                    </label>
                    <input
                      type="text"
                      value={config.wholesale_company_name || ''}
                      onChange={(e) => handleInputChange('wholesale_company_name', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                      placeholder="Enter wholesale company name"
                    />
                    <p className="text-xs text-secondary-gray mt-1">
                      Company name displayed in the wholesale module sidebar.
                    </p>
                  </div>

                  {/* Wholesale Company Email */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Wholesale Company Email
                    </label>
                    <input
                      type="email"
                      value={config.wholesale_company_email || ''}
                      onChange={(e) => handleInputChange('wholesale_company_email', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                      placeholder="wholesale@company.com"
                    />
                  </div>

                  {/* Wholesale Company Phone */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Wholesale Company Phone
                    </label>
                    <input
                      type="text"
                      value={config.wholesale_company_phone || ''}
                      onChange={(e) => handleInputChange('wholesale_company_phone', e.target.value)}
                      className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                      placeholder="+234 800 000 0000"
                    />
                  </div>
                </div>

                {/* Wholesale Company Address */}
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    Wholesale Company Address
                  </label>
                  <textarea
                    value={config.wholesale_company_address || ''}
                    onChange={(e) => handleInputChange('wholesale_company_address', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 resize-none"
                    placeholder="Enter wholesale company address"
                  />
                </div>

                {/* Wholesale Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    Wholesale Logo
                  </label>
                  <div className="flex flex-col sm:flex-row gap-4">
                    {/* Logo Preview */}
                    <div className="w-32 h-32 border-2 border-dashed border-emerald-300 rounded-lg flex items-center justify-center bg-emerald-50 overflow-hidden">
                      {wholesaleLogoPreview ? (
                        <img
                          src={wholesaleLogoPreview}
                          alt="Wholesale logo preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <Image className="w-10 h-10 text-emerald-400" />
                      )}
                    </div>

                    <div className="flex-1 space-y-3">
                      <input
                        ref={wholesaleFileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleWholesaleLogoChange}
                        className="hidden"
                        id="wholesale-logo-upload"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => wholesaleFileInputRef.current?.click()}
                          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          <Upload className="w-4 h-4" />
                          {wholesaleLogoPreview ? 'Change Logo' : 'Upload Logo'}
                        </button>
                        {wholesaleLogoPreview && (
                          <button
                            type="button"
                            onClick={handleRemoveWholesaleLogo}
                            className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-secondary-gray">
                        Recommended: Square image, at least 200x200px. Max size: 5MB.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Wholesale Logo Display Mode */}
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-3">
                    Wholesale Logo Display Mode
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      {
                        value: 'icon',
                        label: 'Icon + Text',
                        desc: 'Small logo with company name text',
                        icon: '🔳 ABC'
                      },
                      {
                        value: 'full',
                        label: 'Logo Only',
                        desc: 'Combined logo that includes company name',
                        icon: '🖼️'
                      },
                      {
                        value: 'none',
                        label: 'Text Only',
                        desc: 'Company name text without logo',
                        icon: 'ABC'
                      }
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${
                          config.wholesale_logo_type === option.value
                            ? 'border-emerald-600 bg-emerald-50'
                            : 'border-secondary-lightGray hover:border-emerald-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="wholesale_logo_type"
                          value={option.value}
                          checked={config.wholesale_logo_type === option.value}
                          onChange={(e) => handleInputChange('wholesale_logo_type', e.target.value)}
                          className="sr-only"
                        />
                        <span className="text-2xl mb-2">{option.icon}</span>
                        <span className="font-medium text-primary-black">{option.label}</span>
                        <span className="text-xs text-secondary-gray">{option.desc}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Wholesale Icon Text */}
                {config.wholesale_logo_type === 'icon' && (
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Wholesale Icon Text
                    </label>
                    <input
                      type="text"
                      value={config.wholesale_logo_icon_text}
                      onChange={(e) =>
                        handleInputChange('wholesale_logo_icon_text', e.target.value.slice(0, 10))
                      }
                      maxLength={10}
                      className="w-full sm:w-48 px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                      placeholder="WS"
                    />
                    <p className="text-xs text-secondary-gray mt-1">
                      Short text displayed in the logo box when no image is uploaded (max 10 chars).
                    </p>
                  </div>
                )}

                {/* Wholesale Sidebar Text Size */}
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    Wholesale Sidebar Text Size
                  </label>
                  <div className="flex bg-gray-100 p-1 rounded-lg w-full sm:w-80">
                    {[
                      { value: 'small', label: 'Small' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'large', label: 'Large' }
                    ].map((size) => (
                      <button
                        key={size.value}
                        type="button"
                        onClick={() => handleInputChange('wholesale_sidebar_name_size', size.value)}
                        className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                          config.wholesale_sidebar_name_size === size.value
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'text-secondary-gray hover:text-primary-black'
                        }`}
                      >
                        {size.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Wholesale Watermark Settings */}
                <div>
                  <label className="flex items-center gap-3 p-3 border border-secondary-lightGray rounded-lg hover:bg-secondary-offWhite transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.wholesale_use_logo_as_watermark}
                      onChange={(e) =>
                        handleInputChange('wholesale_use_logo_as_watermark', e.target.checked)
                      }
                      className="w-4 h-4 text-emerald-600 focus:ring-emerald-600 border-secondary-lightGray rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-primary-black">
                        Use wholesale logo as watermark on reports
                      </span>
                      <p className="text-xs text-secondary-gray">
                        Apply wholesale logo as watermark on wholesale PDFs
                      </p>
                    </div>
                  </label>

                  {config.wholesale_use_logo_as_watermark && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Watermark Opacity
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="0.05"
                          max="0.30"
                          step="0.05"
                          value={config.wholesale_watermark_opacity}
                          onChange={(e) =>
                            handleInputChange(
                              'wholesale_watermark_opacity',
                              parseFloat(e.target.value)
                            )
                          }
                          className="flex-1"
                        />
                        <span className="text-sm text-secondary-gray w-12">
                          {Math.round(config.wholesale_watermark_opacity * 100)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Logo Settings */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4">Logo Settings</h2>

            {/* Logo Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-primary-black mb-2">
                Company Logo
              </label>
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Logo Preview */}
                <div className="w-32 h-32 border-2 border-dashed border-secondary-lightGray rounded-lg flex items-center justify-center bg-secondary-offWhite overflow-hidden">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <Image className="w-10 h-10 text-secondary-gray" />
                  )}
                </div>

                <div className="flex-1 space-y-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleLogoChange}
                    className="hidden"
                    id="logo-upload"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-charcoal text-white rounded-lg hover:bg-primary-black transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    </button>
                    {(logoPreview || config.logo_url) && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        disabled={deleting}
                        className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        {deleting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-secondary-gray">
                    Recommended: Square image, at least 200x200px. Max size: 5MB. Supported formats:
                    JPEG, PNG, GIF, WebP.
                  </p>
                </div>
              </div>
            </div>

            {/* Logo Display Mode */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-primary-black mb-3">
                Logo Display Mode
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  {
                    value: 'icon',
                    label: 'Icon + Text',
                    desc: 'Small logo with company name text',
                    icon: '🔳 ABC'
                  },
                  {
                    value: 'full',
                    label: 'Logo Only',
                    desc: 'Combined logo that includes company name',
                    icon: '🖼️'
                  },
                  {
                    value: 'none',
                    label: 'Text Only',
                    desc: 'Company name text without logo',
                    icon: 'ABC'
                  }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex flex-col p-4 border rounded-lg cursor-pointer transition-all ${
                      config.logo_type === option.value
                        ? 'border-accent-gold bg-accent-gold/5'
                        : 'border-secondary-lightGray hover:border-accent-gold/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="logo_type"
                      value={option.value}
                      checked={config.logo_type === option.value}
                      onChange={(e) => handleInputChange('logo_type', e.target.value)}
                      className="sr-only"
                    />
                    <span className="text-2xl mb-2">{option.icon}</span>
                    <span className="font-medium text-primary-black">{option.label}</span>
                    <span className="text-xs text-secondary-gray">{option.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Icon Text (only shown for 'icon' mode) */}
            {config.logo_type === 'icon' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Icon Text
                </label>
                <input
                  type="text"
                  value={config.logo_icon_text}
                  onChange={(e) => handleInputChange('logo_icon_text', e.target.value.slice(0, 10))}
                  maxLength={10}
                  className="w-full sm:w-48 px-4 py-2 border border-secondary-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold"
                  placeholder="BMS"
                />
                <p className="text-xs text-secondary-gray mt-1">
                  Short text displayed in the logo box when no image is uploaded (max 10 chars).
                </p>
              </div>
            )}
          </div>

          {/* Watermark Settings */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4">Report Watermark</h2>
            <div className="space-y-4">
              <label className="flex items-center gap-3 p-3 border border-secondary-lightGray rounded-lg hover:bg-secondary-offWhite transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.use_logo_as_watermark}
                  onChange={(e) => handleInputChange('use_logo_as_watermark', e.target.checked)}
                  className="w-4 h-4 text-accent-gold focus:ring-accent-gold border-secondary-lightGray rounded"
                />
                <div>
                  <span className="text-sm font-medium text-primary-black">
                    Use logo as watermark on reports
                  </span>
                  <p className="text-xs text-secondary-gray">
                    Apply logo as a subtle background watermark on PDFs
                  </p>
                </div>
              </label>

              {config.use_logo_as_watermark && (
                <div>
                  <label className="block text-sm font-medium text-primary-black mb-2">
                    Watermark Opacity
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0.05"
                      max="0.30"
                      step="0.05"
                      value={config.watermark_opacity}
                      onChange={(e) =>
                        handleInputChange('watermark_opacity', parseFloat(e.target.value))
                      }
                      className="flex-1"
                    />
                    <span className="text-sm text-secondary-gray w-12">
                      {Math.round(config.watermark_opacity * 100)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Brand Colors */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-5 h-5 text-accent-gold" />
              <h2 className="text-lg font-semibold text-primary-black">Brand Colors</h2>
            </div>
            <p className="text-sm text-secondary-gray mb-4">
              These colors are used in receipts, reports, and other printable content.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {/* Primary Color */}
              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Primary Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.primary_color || '#1A1A1A'}
                    onChange={(e) => handleInputChange('primary_color', e.target.value)}
                    className="w-12 h-12 rounded-lg border border-secondary-lightGray cursor-pointer"
                  />
                  <div className="flex-1">
                    <input
                      type="text"
                      value={config.primary_color || '#1A1A1A'}
                      onChange={(e) => handleInputChange('primary_color', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                      placeholder="#1A1A1A"
                    />
                    <p className="text-xs text-secondary-gray mt-1">Headers, borders</p>
                  </div>
                </div>
              </div>

              {/* Accent Color */}
              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Accent Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.accent_color || '#D4AF37'}
                    onChange={(e) => handleInputChange('accent_color', e.target.value)}
                    className="w-12 h-12 rounded-lg border border-secondary-lightGray cursor-pointer"
                  />
                  <div className="flex-1">
                    <input
                      type="text"
                      value={config.accent_color || '#D4AF37'}
                      onChange={(e) => handleInputChange('accent_color', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                      placeholder="#D4AF37"
                    />
                    <p className="text-xs text-secondary-gray mt-1">Highlights, buttons</p>
                  </div>
                </div>
              </div>

              {/* Text Color */}
              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Text Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.text_color || '#333333'}
                    onChange={(e) => handleInputChange('text_color', e.target.value)}
                    className="w-12 h-12 rounded-lg border border-secondary-lightGray cursor-pointer"
                  />
                  <div className="flex-1">
                    <input
                      type="text"
                      value={config.text_color || '#333333'}
                      onChange={(e) => handleInputChange('text_color', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                      placeholder="#333333"
                    />
                    <p className="text-xs text-secondary-gray mt-1">Body text</p>
                  </div>
                </div>
              </div>

              {/* Background Color */}
              <div>
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Background Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.background_color || '#FFFFFF'}
                    onChange={(e) => handleInputChange('background_color', e.target.value)}
                    className="w-12 h-12 rounded-lg border border-secondary-lightGray cursor-pointer"
                  />
                  <div className="flex-1">
                    <input
                      type="text"
                      value={config.background_color || '#FFFFFF'}
                      onChange={(e) => handleInputChange('background_color', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-lightGray rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                      placeholder="#FFFFFF"
                    />
                    <p className="text-xs text-secondary-gray mt-1">White/background</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Color Preview */}
            <div className="mt-4 p-4 border border-secondary-lightGray rounded-lg">
              <p className="text-sm font-medium text-secondary-gray mb-2">Preview</p>
              <div
                className="flex items-center gap-4 p-4 rounded-lg"
                style={{ backgroundColor: config.primary_color || '#1A1A1A' }}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm"
                  style={{
                    backgroundColor: config.accent_color || '#D4AF37',
                    color: config.primary_color || '#1A1A1A'
                  }}
                >
                  {config.logo_icon_text || 'BMS'}
                </div>
                <span className="font-bold text-lg" style={{ color: '#FFFFFF' }}>
                  {config.company_name}
                </span>
              </div>
              <div className="mt-2 p-3 bg-white rounded-lg border border-secondary-lightGray">
                <p className="text-sm" style={{ color: config.text_color || '#333333' }}>
                  Sample text in your text color. This is how content will appear on receipts.
                </p>
              </div>
            </div>
          </div>

          {/* App Colors Section */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-accent-gold" />
                <h2 className="text-lg font-semibold text-primary-black">App Colors</h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  handleInputChange('app_accent_color', '#D4AF37');
                  handleInputChange('app_accent_light', '#FFD700');
                  handleInputChange('app_accent_dark', '#C5A353');
                  handleInputChange('app_success_color', '#10B981');
                  handleInputChange('app_warning_color', '#F59E0B');
                  handleInputChange('app_danger_color', '#EF4444');
                }}
                className="flex items-center gap-1 text-sm text-secondary-gray hover:text-primary-black transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset to Default
              </button>
            </div>

            <p className="text-sm text-secondary-gray mb-4">
              Customize the app's buttons, highlights, and status colors. These changes apply across
              the entire application.
            </p>

            {/* Enable Toggle */}
            <label className="flex items-center gap-3 p-3 mb-4 border border-secondary-lightGray rounded-lg hover:bg-secondary-offWhite transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={config.enable_custom_app_colors}
                onChange={(e) => {
                  handleInputChange('enable_custom_app_colors', e.target.checked);
                  // Update live preview immediately
                  updateConfigLocally({ enable_custom_app_colors: e.target.checked });
                }}
                className="w-4 h-4 text-accent-gold focus:ring-accent-gold border-secondary-lightGray rounded"
              />
              <div>
                <span className="text-sm font-medium text-primary-black">
                  Enable Custom App Colors
                </span>
                <p className="text-xs text-secondary-gray">
                  Override default gold accent with your brand colors
                </p>
              </div>
            </label>

            {config.enable_custom_app_colors && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  {/* Accent Color */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Accent Color
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.app_accent_color || '#D4AF37'}
                        onChange={(e) => {
                          handleInputChange('app_accent_color', e.target.value);
                          updateConfigLocally({ app_accent_color: e.target.value });
                        }}
                        className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.app_accent_color || '#D4AF37'}
                        onChange={(e) => {
                          handleInputChange('app_accent_color', e.target.value);
                          updateConfigLocally({ app_accent_color: e.target.value });
                        }}
                        className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                        placeholder="#D4AF37"
                      />
                    </div>
                    <p className="text-xs text-secondary-gray mt-1">Buttons, links</p>
                  </div>

                  {/* Accent Light */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Accent Light
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.app_accent_light || '#FFD700'}
                        onChange={(e) => {
                          handleInputChange('app_accent_light', e.target.value);
                          updateConfigLocally({ app_accent_light: e.target.value });
                        }}
                        className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.app_accent_light || '#FFD700'}
                        onChange={(e) => {
                          handleInputChange('app_accent_light', e.target.value);
                          updateConfigLocally({ app_accent_light: e.target.value });
                        }}
                        className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                        placeholder="#FFD700"
                      />
                    </div>
                    <p className="text-xs text-secondary-gray mt-1">Hover states</p>
                  </div>

                  {/* Accent Dark */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Accent Dark
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.app_accent_dark || '#C5A353'}
                        onChange={(e) => {
                          handleInputChange('app_accent_dark', e.target.value);
                          updateConfigLocally({ app_accent_dark: e.target.value });
                        }}
                        className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.app_accent_dark || '#C5A353'}
                        onChange={(e) => {
                          handleInputChange('app_accent_dark', e.target.value);
                          updateConfigLocally({ app_accent_dark: e.target.value });
                        }}
                        className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                        placeholder="#C5A353"
                      />
                    </div>
                    <p className="text-xs text-secondary-gray mt-1">Active states</p>
                  </div>

                  {/* Success Color */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Success
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.app_success_color || '#10B981'}
                        onChange={(e) => {
                          handleInputChange('app_success_color', e.target.value);
                          updateConfigLocally({ app_success_color: e.target.value });
                        }}
                        className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.app_success_color || '#10B981'}
                        onChange={(e) => {
                          handleInputChange('app_success_color', e.target.value);
                          updateConfigLocally({ app_success_color: e.target.value });
                        }}
                        className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                        placeholder="#10B981"
                      />
                    </div>
                    <p className="text-xs text-secondary-gray mt-1">Success states</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  {/* Warning Color */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Warning
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.app_warning_color || '#F59E0B'}
                        onChange={(e) => {
                          handleInputChange('app_warning_color', e.target.value);
                          updateConfigLocally({ app_warning_color: e.target.value });
                        }}
                        className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.app_warning_color || '#F59E0B'}
                        onChange={(e) => {
                          handleInputChange('app_warning_color', e.target.value);
                          updateConfigLocally({ app_warning_color: e.target.value });
                        }}
                        className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                        placeholder="#F59E0B"
                      />
                    </div>
                    <p className="text-xs text-secondary-gray mt-1">Warning states</p>
                  </div>

                  {/* Danger Color */}
                  <div>
                    <label className="block text-sm font-medium text-primary-black mb-2">
                      Danger
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.app_danger_color || '#EF4444'}
                        onChange={(e) => {
                          handleInputChange('app_danger_color', e.target.value);
                          updateConfigLocally({ app_danger_color: e.target.value });
                        }}
                        className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                      />
                      <input
                        type="text"
                        value={config.app_danger_color || '#EF4444'}
                        onChange={(e) => {
                          handleInputChange('app_danger_color', e.target.value);
                          updateConfigLocally({ app_danger_color: e.target.value });
                        }}
                        className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-gold"
                        placeholder="#EF4444"
                      />
                    </div>
                    <p className="text-xs text-secondary-gray mt-1">Error states</p>
                  </div>
                </div>

                {/* Live Preview */}
                <div className="mt-4 p-4 border border-secondary-lightGray rounded-lg">
                  <p className="text-sm font-medium text-secondary-gray mb-3">Live Preview</p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg text-sm font-medium text-primary-black transition-colors"
                      style={{ backgroundColor: config.app_accent_color || '#D4AF37' }}
                    >
                      Primary Button
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                      style={{ backgroundColor: config.app_success_color || '#10B981' }}
                    >
                      Success
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                      style={{ backgroundColor: config.app_warning_color || '#F59E0B' }}
                    >
                      Warning
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                      style={{ backgroundColor: config.app_danger_color || '#EF4444' }}
                    >
                      Danger
                    </button>
                  </div>
                  <div className="mt-3 flex gap-2 items-center">
                    <span
                      className="text-sm font-medium"
                      style={{ color: config.app_accent_color || '#D4AF37' }}
                    >
                      Accent Link Text
                    </span>
                    <span className="text-secondary-gray">|</span>
                    <span
                      className="text-sm font-medium"
                      style={{ color: config.app_success_color || '#10B981' }}
                    >
                      ✓ Success Text
                    </span>
                    <span
                      className="text-sm font-medium"
                      style={{ color: config.app_danger_color || '#EF4444' }}
                    >
                      ✕ Error Text
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Retail Module Colors (only shown in retail module) */}
          {activeModule === 'retail' && (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-amber-600" />
                  <h2 className="text-lg font-semibold text-primary-black">Retail Module Colors</h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('retail_primary_color', '#1A1A1A');
                    handleInputChange('retail_accent_color', '#D4AF37');
                    handleInputChange('retail_border_color', '#2D2D2D');
                    handleInputChange('retail_hover_color', '#2D2D2D');
                    handleInputChange('retail_text_color', '#E5E5E5');
                  }}
                  className="flex items-center gap-1 text-sm text-secondary-gray hover:text-primary-black transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset to Default
                </button>
              </div>

              <p className="text-sm text-secondary-gray mb-4">
                Customize the colors for the retail module sidebar and interface. These changes only
                apply to the retail/phase 1 module.
              </p>

              {/* Enable Toggle */}
              <label className="flex items-center gap-3 p-3 mb-4 border border-secondary-lightGray rounded-lg hover:bg-secondary-offWhite transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enable_custom_retail_colors}
                  onChange={(e) => {
                    handleInputChange('enable_custom_retail_colors', e.target.checked);
                    updateConfigLocally({ enable_custom_retail_colors: e.target.checked });
                  }}
                  className="w-4 h-4 text-amber-600 focus:ring-amber-600 border-secondary-lightGray rounded"
                />
                <div>
                  <span className="text-sm font-medium text-primary-black">
                    Enable Custom Retail Colors
                  </span>
                  <p className="text-xs text-secondary-gray">
                    Override default dark/gold theme with your brand colors
                  </p>
                </div>
              </label>

              {config.enable_custom_retail_colors && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    {/* Primary Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Primary Background
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.retail_primary_color || '#1A1A1A'}
                          onChange={(e) => {
                            handleInputChange('retail_primary_color', e.target.value);
                            updateConfigLocally({ retail_primary_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.retail_primary_color || '#1A1A1A'}
                          onChange={(e) => {
                            handleInputChange('retail_primary_color', e.target.value);
                            updateConfigLocally({ retail_primary_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-600"
                          placeholder="#1A1A1A"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Sidebar background</p>
                    </div>

                    {/* Accent Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Accent Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.retail_accent_color || '#D4AF37'}
                          onChange={(e) => {
                            handleInputChange('retail_accent_color', e.target.value);
                            updateConfigLocally({ retail_accent_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.retail_accent_color || '#D4AF37'}
                          onChange={(e) => {
                            handleInputChange('retail_accent_color', e.target.value);
                            updateConfigLocally({ retail_accent_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-600"
                          placeholder="#D4AF37"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Active states, logo bg</p>
                    </div>

                    {/* Border Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Border Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.retail_border_color || '#2D2D2D'}
                          onChange={(e) => {
                            handleInputChange('retail_border_color', e.target.value);
                            updateConfigLocally({ retail_border_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.retail_border_color || '#2D2D2D'}
                          onChange={(e) => {
                            handleInputChange('retail_border_color', e.target.value);
                            updateConfigLocally({ retail_border_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-600"
                          placeholder="#2D2D2D"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Dividers, borders</p>
                    </div>

                    {/* Hover Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Hover Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.retail_hover_color || '#2D2D2D'}
                          onChange={(e) => {
                            handleInputChange('retail_hover_color', e.target.value);
                            updateConfigLocally({ retail_hover_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.retail_hover_color || '#2D2D2D'}
                          onChange={(e) => {
                            handleInputChange('retail_hover_color', e.target.value);
                            updateConfigLocally({ retail_hover_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-600"
                          placeholder="#2D2D2D"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Hover states</p>
                    </div>

                    {/* Text Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Text Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.retail_text_color || '#E5E5E5'}
                          onChange={(e) => {
                            handleInputChange('retail_text_color', e.target.value);
                            updateConfigLocally({ retail_text_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.retail_text_color || '#E5E5E5'}
                          onChange={(e) => {
                            handleInputChange('retail_text_color', e.target.value);
                            updateConfigLocally({ retail_text_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-600"
                          placeholder="#E5E5E5"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Menu text, links</p>
                    </div>
                  </div>

                  {/* Live Preview */}
                  <div className="mt-4 p-4 border border-secondary-lightGray rounded-lg">
                    <p className="text-sm font-medium text-secondary-gray mb-3">
                      Retail Sidebar Preview
                    </p>
                    <div
                      className="rounded-lg p-4"
                      style={{ backgroundColor: config.retail_primary_color || '#1A1A1A' }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: config.retail_accent_color || '#D4AF37' }}
                        >
                          <span className="text-primary-black font-bold text-sm">BMS</span>
                        </div>
                        <span className="text-white font-bold text-lg">{config.company_name}</span>
                      </div>
                      <div
                        className="p-3 rounded-lg mb-2"
                        style={{ backgroundColor: config.retail_accent_color || '#D4AF37' }}
                      >
                        <span className="text-primary-black text-sm font-medium">
                          Active Menu Item
                        </span>
                      </div>
                      <div
                        className="p-3 rounded-lg"
                        style={{
                          backgroundColor: 'transparent',
                          color: config.retail_text_color || '#E5E5E5'
                        }}
                      >
                        <span className="text-sm">Inactive Menu Item</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Wholesale Module Colors (only shown in wholesale module) */}
          {activeModule === 'wholesale' && (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-lg font-semibold text-primary-black">
                    Wholesale Module Colors
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('wholesale_primary_color', '#047857');
                    handleInputChange('wholesale_accent_color', '#10B981');
                    handleInputChange('wholesale_border_color', '#065F46');
                    handleInputChange('wholesale_hover_color', '#065F46');
                    handleInputChange('wholesale_text_color', '#D1FAE5');
                  }}
                  className="flex items-center gap-1 text-sm text-secondary-gray hover:text-primary-black transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset to Default
                </button>
              </div>

              <p className="text-sm text-secondary-gray mb-4">
                Customize the colors for the wholesale module sidebar and interface. These changes
                only apply to the wholesale/phase 2 module.
              </p>

              {/* Enable Toggle */}
              <label className="flex items-center gap-3 p-3 mb-4 border border-secondary-lightGray rounded-lg hover:bg-secondary-offWhite transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enable_custom_wholesale_colors}
                  onChange={(e) => {
                    handleInputChange('enable_custom_wholesale_colors', e.target.checked);
                    updateConfigLocally({ enable_custom_wholesale_colors: e.target.checked });
                  }}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-600 border-secondary-lightGray rounded"
                />
                <div>
                  <span className="text-sm font-medium text-primary-black">
                    Enable Custom Wholesale Colors
                  </span>
                  <p className="text-xs text-secondary-gray">
                    Override default emerald green with your brand colors
                  </p>
                </div>
              </label>

              {config.enable_custom_wholesale_colors && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    {/* Primary Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Primary Background
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.wholesale_primary_color || '#047857'}
                          onChange={(e) => {
                            handleInputChange('wholesale_primary_color', e.target.value);
                            updateConfigLocally({ wholesale_primary_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.wholesale_primary_color || '#047857'}
                          onChange={(e) => {
                            handleInputChange('wholesale_primary_color', e.target.value);
                            updateConfigLocally({ wholesale_primary_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-600"
                          placeholder="#047857"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Sidebar background</p>
                    </div>

                    {/* Accent Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Accent Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.wholesale_accent_color || '#10B981'}
                          onChange={(e) => {
                            handleInputChange('wholesale_accent_color', e.target.value);
                            updateConfigLocally({ wholesale_accent_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.wholesale_accent_color || '#10B981'}
                          onChange={(e) => {
                            handleInputChange('wholesale_accent_color', e.target.value);
                            updateConfigLocally({ wholesale_accent_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-600"
                          placeholder="#10B981"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Active states, logo bg</p>
                    </div>

                    {/* Border Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Border Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.wholesale_border_color || '#065F46'}
                          onChange={(e) => {
                            handleInputChange('wholesale_border_color', e.target.value);
                            updateConfigLocally({ wholesale_border_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.wholesale_border_color || '#065F46'}
                          onChange={(e) => {
                            handleInputChange('wholesale_border_color', e.target.value);
                            updateConfigLocally({ wholesale_border_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-600"
                          placeholder="#065F46"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Dividers, borders</p>
                    </div>

                    {/* Hover Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Hover Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.wholesale_hover_color || '#065F46'}
                          onChange={(e) => {
                            handleInputChange('wholesale_hover_color', e.target.value);
                            updateConfigLocally({ wholesale_hover_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.wholesale_hover_color || '#065F46'}
                          onChange={(e) => {
                            handleInputChange('wholesale_hover_color', e.target.value);
                            updateConfigLocally({ wholesale_hover_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-600"
                          placeholder="#065F46"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Hover states</p>
                    </div>

                    {/* Text Color */}
                    <div>
                      <label className="block text-sm font-medium text-primary-black mb-2">
                        Text Color
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={config.wholesale_text_color || '#D1FAE5'}
                          onChange={(e) => {
                            handleInputChange('wholesale_text_color', e.target.value);
                            updateConfigLocally({ wholesale_text_color: e.target.value });
                          }}
                          className="w-10 h-10 rounded-lg border border-secondary-lightGray cursor-pointer"
                        />
                        <input
                          type="text"
                          value={config.wholesale_text_color || '#D1FAE5'}
                          onChange={(e) => {
                            handleInputChange('wholesale_text_color', e.target.value);
                            updateConfigLocally({ wholesale_text_color: e.target.value });
                          }}
                          className="flex-1 px-2 py-1 border border-secondary-lightGray rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-600"
                          placeholder="#D1FAE5"
                        />
                      </div>
                      <p className="text-xs text-secondary-gray mt-1">Menu text, links</p>
                    </div>
                  </div>

                  {/* Live Preview */}
                  <div className="mt-4 p-4 border border-secondary-lightGray rounded-lg">
                    <p className="text-sm font-medium text-secondary-gray mb-3">
                      Wholesale Sidebar Preview
                    </p>
                    <div
                      className="rounded-lg p-4"
                      style={{ backgroundColor: config.wholesale_primary_color || '#047857' }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: config.wholesale_accent_color || '#10B981' }}
                        >
                          <span className="text-white font-bold text-sm">WS</span>
                        </div>
                        <span className="text-white font-bold text-lg">Wholesale</span>
                      </div>
                      <div
                        className="p-3 rounded-lg mb-2"
                        style={{ backgroundColor: config.wholesale_accent_color || '#10B981' }}
                      >
                        <span className="text-white text-sm font-medium">Active Menu Item</span>
                      </div>
                      <div
                        className="p-3 rounded-lg"
                        style={{
                          backgroundColor: 'transparent',
                          color: config.wholesale_text_color || '#D1FAE5'
                        }}
                      >
                        <span className="text-sm">Inactive Menu Item</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Preview */}
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-primary-black mb-4">Sidebar Preview</h2>
            <div className="bg-primary-black rounded-lg p-4 max-w-xs">
              <div className="flex items-center gap-3">
                {config.logo_type === 'none' ? (
                  <span className="text-white font-bold text-xl truncate">
                    {config.company_name}
                  </span>
                ) : config.logo_type === 'full' && logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="h-10 max-w-full object-contain" />
                ) : (
                  <>
                    <div className="w-10 h-10 bg-accent-gold rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-primary-black font-bold text-sm">
                          {config.logo_icon_text || 'BMS'}
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-white font-bold leading-tight break-words ${
                        config.sidebar_name_size === 'small'
                          ? 'text-base'
                          : config.sidebar_name_size === 'large'
                            ? 'text-2xl'
                            : 'text-xl'
                      }`}
                    >
                      {config.company_name}
                    </span>
                  </>
                )}
              </div>
            </div>
            <p className="text-xs text-secondary-gray mt-3">
              This is how your branding will appear in the sidebar navigation.
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Save Settings</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandingSettingsPage;
